import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useBetaSignup } from '../context/BetaSignupContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Sparkles, CheckCircle, Loader2, Copy, Twitter, Linkedin, Share2, Users } from 'lucide-react';

const BetaSignupModal = () => {
  const { isModalOpen, closeModal, referralCode: contextReferralCode } = useBetaSignup();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    investmentAmount: '',
    ageRange: '',
    investingExperience: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [successData, setSuccessData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [referrerName, setReferrerName] = useState(null);

  // Get referral code from URL or context
  const urlReferralCode = searchParams.get('ref') || contextReferralCode;

  // Check if referral code is valid on mount
  useEffect(() => {
    const verifyReferral = async () => {
      if (urlReferralCode) {
        try {
          const API_URL = process.env.REACT_APP_BACKEND_URL;
          const response = await fetch(`${API_URL}/api/beta-signup/verify/${urlReferralCode}`);
          const data = await response.json();
          if (data.valid) {
            setReferrerName(data.referrer_name);
          }
        } catch (err) {
          console.error('Error verifying referral:', err);
        }
      }
    };
    verifyReferral();
  }, [urlReferralCode]);

  const investmentOptions = [
    { value: 'under-5k', label: 'Under $5,000' },
    { value: '5k-25k', label: '$5,000 - $25,000' },
    { value: '25k-50k', label: '$25,000 - $50,000' },
    { value: '50k-100k', label: '$50,000 - $100,000' },
    { value: '100k-250k', label: '$100,000 - $250,000' },
    { value: '250k-plus', label: '$250,000+' }
  ];

  const ageRangeOptions = [
    { value: '18-24', label: '18-24' },
    { value: '25-34', label: '25-34' },
    { value: '35-44', label: '35-44' },
    { value: '44+', label: '44+' }
  ];

  const experienceOptions = [
    { value: 'just-starting', label: 'Just getting started', sublabel: 'Less than 1 year' },
    { value: 'some-experience', label: 'Some Experience', sublabel: '1-3 years' },
    { value: 'experienced', label: 'Experienced', sublabel: '3-5 years' },
    { value: 'sophisticated', label: 'Sophisticated Investor', sublabel: '5 years+' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSelectChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate Full Name (at least 2 characters, letters and spaces only)
    if (!formData.fullName || formData.fullName.trim().length < 2) {
      setError('Please enter your full name (at least 2 characters)');
      return;
    }
    if (!/^[a-zA-Z\s'-]+$/.test(formData.fullName.trim())) {
      setError('Full name can only contain letters, spaces, hyphens and apostrophes');
      return;
    }

    // Validate Email
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Validate Australian Phone Number
    // Remove all spaces, dashes, and parentheses for validation
    const cleanPhone = formData.phone.replace(/[\s\-()]/g, '');
    // Australian mobile: 04XXXXXXXX (10 digits) or +614XXXXXXXX (12 chars)
    // Australian landline: 0XXXXXXXXX (10 digits) or +61XXXXXXXXX (11-12 chars)
    const ausPhoneRegex = /^(\+61|0)[2-9]\d{8}$|^(\+61|0)4\d{8}$/;
    if (!formData.phone || !ausPhoneRegex.test(cleanPhone)) {
      setError('Please enter a valid Australian phone number (e.g., 0412 345 678 or +61 412 345 678)');
      return;
    }

    // Validate other required fields
    if (!formData.investmentAmount) {
      setError('Please select an investment amount');
      return;
    }
    if (!formData.ageRange) {
      setError('Please select your age range');
      return;
    }
    if (!formData.investingExperience) {
      setError('Please select your investing experience');
      return;
    }

    setIsSubmitting(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/beta-signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          investmentAmount: formData.investmentAmount,
          ageRange: formData.ageRange,
          investingExperience: formData.investingExperience,
          referralCode: urlReferralCode || null
        }),
      });

      const text = await response.text();
      let data;
      
      try {
        data = JSON.parse(text);
      } catch (e) {
        throw new Error('Invalid response from server');
      }

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit');
      }

      setSuccessData(data);
      setIsSuccess(true);
    } catch (err) {
      console.error('Beta signup error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    closeModal();
    setTimeout(() => {
      setFormData({ fullName: '', email: '', phone: '', investmentAmount: '', ageRange: '', investingExperience: '' });
      setIsSuccess(false);
      setSuccessData(null);
      setError('');
      setCopied(false);
    }, 300);
  };

  const getReferralUrl = () => {
    if (!successData?.referral_code) return '';
    const baseUrl = window.location.origin;
    return `${baseUrl}?ref=${successData.referral_code}`;
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(getReferralUrl());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const shareOnTwitter = () => {
    const text = `I just joined the @TroveWealth beta waitlist! Join me and skip the queue 🚀`;
    const url = getReferralUrl();
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
  };

  const shareOnLinkedIn = () => {
    const url = getReferralUrl();
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join Trove Beta',
          text: 'I just joined the Trove beta waitlist! Join me and skip the queue.',
          url: getReferralUrl()
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    }
  };

  return (
    <Dialog open={isModalOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md bg-[#F5F3ED] border-0 max-h-[90vh] overflow-y-auto" data-testid="beta-signup-modal">
        {isSuccess && successData ? (
          <div className="py-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <DialogTitle className="text-2xl font-light mb-2" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }}>
              You're in! 🎉
            </DialogTitle>
            
            {/* Queue Position */}
            <div className="bg-[#1a1a3e] text-white rounded-2xl p-6 my-6">
              <p className="text-sm uppercase tracking-wider text-gray-300 mb-1">Your position in queue</p>
              <p className="text-5xl font-light" style={{ fontFamily: 'Georgia, serif' }}>
                #{successData.queue_position}
              </p>
              {successData.referral_count > 0 && (
                <p className="text-sm text-[#FF8C42] mt-2">
                  🔥 {successData.referral_count} referral{successData.referral_count > 1 ? 's' : ''} - you've moved up!
                </p>
              )}
            </div>

            {/* Referral Section */}
            <div className="bg-white rounded-xl p-5 mb-6 text-left">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-5 h-5 text-[#FF8C42]" />
                <h3 className="font-medium text-[#1a1a3e]">Skip the queue!</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Share your unique link. For every friend who joins, you move up 1 spot.
              </p>
              
              {/* Referral Link */}
              <div className="flex items-center gap-2 mb-4">
                <Input
                  value={getReferralUrl()}
                  readOnly
                  className="text-sm bg-gray-50 flex-1"
                />
                <Button
                  onClick={copyToClipboard}
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                >
                  {copied ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>

              {/* Share Buttons */}
              <div className="flex gap-2">
                <Button
                  onClick={shareOnTwitter}
                  variant="outline"
                  size="sm"
                  className="flex-1 text-xs"
                >
                  <Twitter className="w-4 h-4 mr-1" /> Twitter
                </Button>
                <Button
                  onClick={shareOnLinkedIn}
                  variant="outline"
                  size="sm"
                  className="flex-1 text-xs"
                >
                  <Linkedin className="w-4 h-4 mr-1" /> LinkedIn
                </Button>
                {navigator.share && (
                  <Button
                    onClick={shareNative}
                    variant="outline"
                    size="sm"
                    className="flex-1 text-xs"
                  >
                    <Share2 className="w-4 h-4 mr-1" /> Share
                  </Button>
                )}
              </div>
            </div>

            <DialogDescription className="text-gray-600 text-sm mb-4">
              We'll be in touch soon with exclusive early access details.
            </DialogDescription>
            
            <Button 
              onClick={handleClose}
              className="bg-[#1a1a3e] hover:bg-[#252550] rounded-full px-8"
              data-testid="beta-modal-close-btn"
            >
              Done
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader className="text-center pb-2">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#FF8C42]/10 flex items-center justify-center">
                <Sparkles className="w-7 h-7 text-[#FF8C42]" />
              </div>
              <DialogTitle className="text-2xl font-light" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }}>
                Join our beta cohort
              </DialogTitle>
              <DialogDescription className="text-gray-600 mt-2">
                {referrerName ? (
                  <span className="text-[#FF8C42] font-medium">{referrerName} invited you!</span>
                ) : (
                  'Our early access program launches in 2026.'
                )}
                {' '}Get priority access, product updates, and early invites.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="fullName" className="text-sm font-medium text-gray-700">
                  Full Name
                </Label>
                <Input
                  id="fullName"
                  name="fullName"
                  type="text"
                  placeholder="John Smith"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className="h-11 bg-white border-gray-300 focus:border-[#FF8C42] focus:ring-[#FF8C42]"
                  data-testid="beta-fullname-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                  Email
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="h-11 bg-white border-gray-300 focus:border-[#FF8C42] focus:ring-[#FF8C42]"
                  data-testid="beta-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-sm font-medium text-gray-700">
                  Phone
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+61 400 000 000"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="h-11 bg-white border-gray-300 focus:border-[#FF8C42] focus:ring-[#FF8C42]"
                  data-testid="beta-phone-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="investmentAmount" className="text-sm font-medium text-gray-700">
                  Estimated Investment Amount
                </Label>
                <Select 
                  value={formData.investmentAmount} 
                  onValueChange={(value) => handleSelectChange('investmentAmount', value)}
                >
                  <SelectTrigger 
                    className="h-11 bg-white border-gray-300 focus:border-[#FF8C42] focus:ring-[#FF8C42]"
                    data-testid="beta-investment-select"
                  >
                    <SelectValue placeholder="Select amount range" />
                  </SelectTrigger>
                  <SelectContent>
                    {investmentOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ageRange" className="text-sm font-medium text-gray-700">
                  How old are you?
                </Label>
                <Select 
                  value={formData.ageRange} 
                  onValueChange={(value) => handleSelectChange('ageRange', value)}
                >
                  <SelectTrigger 
                    className="h-11 bg-white border-gray-300 focus:border-[#FF8C42] focus:ring-[#FF8C42]"
                    data-testid="beta-age-select"
                  >
                    <SelectValue placeholder="Select age range" />
                  </SelectTrigger>
                  <SelectContent>
                    {ageRangeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="investingExperience" className="text-sm font-medium text-gray-700">
                  How many years have you been investing?
                </Label>
                <Select 
                  value={formData.investingExperience} 
                  onValueChange={(value) => handleSelectChange('investingExperience', value)}
                >
                  <SelectTrigger 
                    className="h-11 bg-white border-gray-300 focus:border-[#FF8C42] focus:ring-[#FF8C42]"
                    data-testid="beta-experience-select"
                  >
                    <SelectValue placeholder="Select experience level" />
                  </SelectTrigger>
                  <SelectContent>
                    {experienceOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex flex-col">
                          <span>{option.label}</span>
                          <span className="text-xs text-gray-500">{option.sublabel}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {error && (
                <p className="text-sm text-red-600 text-center" data-testid="beta-error-message">
                  {error}
                </p>
              )}

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-12 bg-[#FF8C42] hover:bg-[#FFB366] text-white rounded-full font-medium text-base mt-6"
                data-testid="beta-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Join the Beta'
                )}
              </Button>

              <p className="text-xs text-gray-500 text-center mt-4">
                By joining, you agree to receive updates about Trove. We respect your privacy.
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BetaSignupModal;
